<template>
  <div class="ai-ppt-generator" ref="generatorRef">
    <Img
      class="ai-ppt-generator__background"
      :enable-oss="false"
      :src="
        getFeUrl(
          `/youyan-platform/ai-ppt/banner/generator-${activeTab !== 'document' ? 'prompt' : 'script'}.png`,
        )
      "
    />
    <div class="generator-content">
      <div class="generator-input-wrapper">
        <!-- Tab 切换 -->
        <div class="input-container">
          <div class="input-tabs">
            <div
              v-for="tab in tabs"
              :key="tab.value"
              class="tab-item"
              :class="{ active: activeTab === tab.value }"
              @click="activeTab = tab.value"
            >
              <component :is="tab.icon" />
              <span class="tab-label">{{ tab.label }}</span>
            </div>
          </div>
          <!-- 想法输入区域 -->
          <div class="input-box" :class="`input-box--${activeTab}`">
            <div class="input-content" v-if="activeTab !== 'document'">
              <textarea
                v-if="activeTab === 'idea'"
                v-model="ideaInputText"
                class="text-input"
                :placeholder="ideaPlaceholder"
                :maxlength="maxLength"
                rows="6"
              />
              <textarea
                v-if="activeTab === 'script'"
                v-model="scriptInputText"
                class="text-input"
                placeholder="在此输入演讲稿，或粘贴已经准备好的文本内容，我们将为你智能生成对应的PPT模版以及配图"
                :maxlength="maxLength"
                rows="6"
              />
              <div class="input-footer" v-if="activeTab === 'idea' || activeTab === 'script'">
                <div class="action-section">
                  <span class="char-count">{{ inputText.length }}/{{ maxLength }}</span>
                  <div class="divider" />
                  <button
                    class="send-btn"
                    :disabled="activeTab !== 'idea' && !inputText.trim()"
                    @click="handleSend"
                  >
                    <SendIcon />
                  </button>
                </div>
                <div class="filter-section" v-if="activeTab === 'idea'">
                  <Dropdown
                    v-for="(filter, index) in filters"
                    :key="filter.key"
                    ref="dropdownRefs"
                    strategy="absolute"
                  >
                    <template #trigger="{ active }">
                      <div class="filter-item">
                        <component :is="filter.icon" class="filter-icon" />
                        <span class="filter-label"
                          >{{ filter.selected }}
                          <DropdownIcon class="dropdown-icon" :class="{ active }" />
                        </span>
                      </div>
                    </template>
                    <template #content>
                      <div class="filter-dropdown-content">
                        <p
                          class="filter-dropdown-item"
                          :class="{ active: option.value === filter.selected }"
                          v-for="option in filter.options"
                          :key="option.value"
                          @click="handleFilterSelect(index, option.value)"
                        >
                          {{ option.value }}
                        </p>
                      </div>
                    </template>
                  </Dropdown>
                </div>
                <button
                  class="ai-script-section"
                  v-else-if="activeTab === 'script'"
                  @click="handleAiGen"
                >
                  <AiIcon class="ai-icon" />
                  <span>帮我写讲稿</span>
                </button>
              </div>
            </div>
            <div class="upload-section" v-else>
              <input
                type="file"
                ref="fileInput"
                class="hidden-file-input"
                accept=".doc,.docx,.pdf,.txt"
                @change="handleFileChange"
              />
              <template v-if="!isParsing">
                <div class="upload-filetype upload-filetype--doc" @click="triggerUpload('doc')">
                  <DocIcon class="upload-icon" />
                  <p class="type-name">Word文档</p>
                </div>
                <div class="upload-filetype upload-filetype--pdf" @click="triggerUpload('pdf')">
                  <PdfIcon class="upload-icon" />
                  <p class="type-name">PDF文档</p>
                </div>
                <div class="upload-filetype upload-filetype--txt" @click="triggerUpload('txt')">
                  <TxtIcon class="upload-icon" />
                  <p class="type-name">TXT文档</p>
                </div>
              </template>
              <div v-else class="parsing-status">
                <LoadingFill class="loading-spinner" />
                <p class="parsing-text">正在解析文档，请稍候...</p>
              </div>
            </div>
          </div>
        </div>

        <!-- 热门标签 -->
        <div class="popular-tags" v-show="activeTab === 'idea' || activeTab === 'script'">
          <span class="tags-label">{{ currentPopularTagsLabel }}</span>
          <div class="tags-list">
            <div
              v-for="tag in currentPopularTags"
              :key="tag.text"
              class="tag-item"
              @click="handleApplyTag(tag)"
            >
              <span class="tag-emoji">{{ tag.emoji }}</span>
              <span class="tag-text">{{ tag.text }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { fetchParseFileResult, parseFile } from '@/api/module/media';
import DocIcon from '@/design/icons/doc.svg?component';
import DropdownIcon from '@/design/icons/dropdown.svg?component';
import PdfIcon from '@/design/icons/pdf.svg?component';
import AiIcon from '@/design/icons/ppt-ai.svg?component';
import AudienceIcon from '@/design/icons/ppt-audience.svg?component';
import DocumentIcon from '@/design/icons/ppt-document.svg?component';
import IdeaIcon from '@/design/icons/ppt-idea.svg?component';
import PagesIcon from '@/design/icons/ppt-pages.svg?component';
import SceneIcon from '@/design/icons/ppt-scene.svg?component';
import ScriptIcon from '@/design/icons/ppt-script.svg?component';
import SendIcon from '@/design/icons/ppt-send.svg?component';
import TxtIcon from '@/design/icons/txt.svg?component';
import { useAccountLogin } from '@/pages/login/shared/lib/useAccountLogin';
import { useUserStore } from '@rendora/common/store/user';
import Dropdown from '@rendora/design/components/dropdown/Dropdown.vue';
import Img from '@rendora/design/components/img/Img.vue';
import LoadingFill from '~/assets/svg/loading-fill.svg';

const { show } = useAccountLogin();
const userStore = useUserStore();

import { clear, del, get, getMany, set } from 'idb-keyval';

const DB = { set, get, del, clear, getMany };

type TabValue = 'idea' | 'script' | 'document';

interface TagContent {
  key: string;
  emoji: string;
  text: string;
  content: string;
}

const activeTab = ref<TabValue>('idea');
const ideaInputText = ref('');
const scriptInputText = ref('');
const isParsing = ref(false);

const inputText = computed({
  get() {
    return activeTab.value === 'idea' ? ideaInputText.value : scriptInputText.value;
  },
  set(value: string) {
    if (activeTab.value === 'idea') {
      ideaInputText.value = value;
    } else {
      scriptInputText.value = value;
    }
  },
});

const tabs: Array<{ value: TabValue; label: string; icon: Component }> = [
  { value: 'idea', label: '输入想法生成PPT', icon: markRaw(IdeaIcon) },
  { value: 'script', label: '输入讲稿生成PPT', icon: markRaw(ScriptIcon) },
  { value: 'document', label: '导入文档生成PPT', icon: markRaw(DocumentIcon) },
];

const promptOptions = {
  page_range: [
    { value: '智能生成' },
    { value: '10页以内' },
    { value: '10～15页' },
    { value: '15～20页' },
    { value: '20页以上' },
  ],
  audience: [
    { value: '大众' },
    { value: '投资者' },
    { value: '学生' },
    { value: '教师' },
    { value: '面试官' },
    { value: '员工' },
    { value: '同事同行' },
  ],
  scene: [
    { value: '通用' },
    { value: '公共演讲' },
    { value: '教学课件' },
    { value: '宣传材料' },
    { value: '研究报告' },
    { value: '项目汇报' },
    { value: '解决方案' },
    { value: '产品介绍' },
    { value: '年度总结' },
  ],
};

const filters = reactive([
  {
    icon: markRaw(PagesIcon),
    selected: '智能生成',
    options: promptOptions.page_range,
    key: 'pages',
  },
  {
    icon: markRaw(AudienceIcon),
    selected: '大众',
    options: promptOptions.audience,
    key: 'audience',
  },
  { icon: markRaw(SceneIcon), selected: '通用', options: promptOptions.scene, key: 'scene' },
]);

const handleFilterSelect = (filterIndex: number, option: string) => {
  filters[filterIndex].selected = option;
  // 关闭对应的 dropdown
  dropdownRefs.value[filterIndex]?.close();
};

const ideaPopularTags: TagContent[] = [
  {
    key: 'teacher-courseware',
    emoji: '🎓',
    text: '教师课程课件',
    content: '生成一个小学语文课程中关于《静夜思》的课件PPT',
  },
  {
    key: 'annual-summary',
    emoji: '📊',
    text: '年终工作总结',
    content: '生成一个销售部门的年终工作总结汇报PPT',
  },
  {
    key: 'competitor-analysis',
    emoji: '🔍',
    text: '竞品分析报告',
    content: '生成一个微单相机行业的竞品分析报告PPT',
  },
  {
    key: 'media-operation',
    emoji: '🎬',
    text: '自媒体运营教学',
    content: '生成一个自媒体运营教学的课件PPT',
  },
  {
    key: 'onboarding-training',
    emoji: '📚',
    text: '新人入职培训',
    content: '生成一个面向公司新人的入职培训PPT',
  },
];

const scriptPopularTags: TagContent[] = [
  {
    key: 'teacher-courseware',
    emoji: '🎓',
    text: '教师课程课件',
    content: `各位同学，今天我们要走进唐代大诗人李白的千古名篇《静夜思》。大家先和老师一起读题目：静夜思，“思”是思念的意思，猜猜诗人在安静的夜晚思念什么呢？
首先认识诗人李白，他被称为“诗仙”，写过很多优美的诗，这首诗是他在外地旅行时，看着明月写下的。现在跟着拼音齐读古诗：床前明月光，疑是地上霜。举头望明月，低头思故乡。
注意这几个生字：“疑”是怀疑、好像的意思，左边是“匕”，右边是“疑”；“举”要抬头做动作记住，和“举手”的举一样；“乡”是后鼻音，故乡就是我们的老家。
我们逐句理解：第一句“床前明月光”，诗人在床前看到明亮的月光；“疑是地上霜”，这月光白白的，让他以为地上结了霜，多清冷的夜晚呀！再看后两句，大家一起做“举头”的动作，抬头，诗人望着天上的明月；然后“低头”，他在想什么呢？对啦，在思念故乡的亲人、朋友和家乡的风景。
其实当我们离开家，想念爸爸妈妈时，也会有这样的心情。希望大家记住这首诗，以后看到明月，就能想起李白的思乡之情啦。`,
  },
  {
    key: 'annual-summary',
    emoji: '📊',
    text: '年终工作总结',
    content: `尊敬的领导、各位同事：大家好！现将销售部门2025年度工作简要汇报如下：
我们全年累计完成销售额100万元，超额达成年度目标，同比增长20%。核心市场稳步发力，新兴市场实现突破，新增客户50家，核心客户留存率提升至13%，核心产品与新品均贡献显著业绩。
工作中，我们优化销售策略，动态调整定价与优惠政策；拓展线上线下渠道，新增线下网点10个，线上销售额同比增长30%；强化客户分层服务，问题解决率达50%；开展专业培训70场，完善激励机制，凝聚团队合力。
同时我们也清醒认识到不足：新兴市场渗透率不足，部分人员高端客户服务能力有待提升，客户信息分析应用不充分。
2026年，我们将冲刺销售额200万元，加大新兴市场资源投入，推行分层培训与导师带教机制，深化客户精细化管理，提升单客价值。
新一年，我们将凝心聚力、攻坚克难，感谢领导支持与各部门协作！汇报完毕，谢谢大家！`,
  },
  {
    key: 'competitor-analysis',
    emoji: '🔍',
    text: '竞品分析报告',
    content: `现在微单已经成为可换镜头相机的主流，全球销量占比超过百分之七十，单反市场一直在萎缩。目前核心竞品主要集中在五个品牌，每个品牌都有自己明确的定位。
索尼的核心优势在全画幅机型，像A7系列和A6700这些机型，有着高速对焦和7K超采样的视频拍摄能力，领跑专业市场。它的AI主体追踪技术在行业里处于领先地位，不过镜头价格相对偏高。
佳能靠均衡的表现突出重围，第二代R6的连拍速度能达到每秒四十张，R50这类入门机型，有4K无裁切和机身防抖功能，很适合普通用户。佳能的用户基数大，镜头群扩展得也快。
富士走的是差异化路线，XM5和XT5的胶片模拟功能，还有复古设计，特别受文艺创作者喜欢。6.2K的视频录制能满足专业创作需求，不过它的高感表现一般。
尼康第三代Z6以高性价比进入入门全画幅市场，操作简单容易上手。
核心差异主要在画质，便携性和视频性能上。全画幅机型比拼传感器和对焦能力，半画幅机型竞争轻量化和性价比。视频创作方面，大家都在关注4K或者6K分辨率，还有Log格式支持。
未来的趋势还是轻量化，AI智能对焦和专业视频功能。镜头群的完善程度和性价比，会决定市场份额的走向。建议重点关注细分场景的需求，来精准匹配产品策略。`,
  },
  {
    key: 'media-operation',
    emoji: '🎬',
    text: '自媒体运营教学',
    content: `今天我们快速掌握自媒体运营的核心逻辑，新手入门关键抓四点：定位、内容、运营、变现。
首先是精准定位，这是根基。要选你擅长又有市场需求的垂直领域，比如“上班族减脂餐”“租房收纳技巧”，越细分越容易出圈。再立一个鲜明人设，用一句话说清“你是谁、能提供什么价值”，比如“90后宝妈低成本育儿博主”。平台不用多，先深耕一个，短视频选抖音、视频号，图文选小红书，深度内容选B站。
接着是内容创作，这是核心竞争力。要建选题库，收集用户痛点、热点和干货，提前备20个左右选题。爆款内容有固定框架：短视频前3秒用悬念或视觉冲击抓注意力，中间15秒输出核心价值，结尾引导点赞关注；图文要结论前置，信息密度高。新手可先用剪映，搭配AI工具辅助创作，降低门槛，记得保持原创和稳定更新，每周3到5条最佳。
然后是精细运营，做好数据驱动。24小时内回复评论私信，提升用户粘性；重点盯完播率、互动率，3天没起色就优化标题或封面，每周复盘迭代。冷启动时可对标同赛道爆款，和同量级博主互推，优质内容可小额投流加热。
最后是合规变现，分阶段推进。0到1万粉先靠平台激励、商品佣金；1到10万粉接品牌商单；粉丝再多可做知识付费或私域变现。记住要遵守平台规则，不盲目追热点，真实有温度的账号才能走得远。
自媒体没有捷径，坚持价值输出和持续优化，3个月就能看到明显效果。祝大家都能做出优质账号！`,
  },
  {
    key: 'onboarding-training',
    emoji: '📚',
    text: '新人入职培训',
    content: `各位新同事，大家好！
欢迎加入公司这个温暖的大家庭，从今天起，我们就是并肩奋斗的伙伴了。首先，简单了解下公司：我们专注于科技研发领域，经过多年发展，已成为行业内有影响力的团队，这里既有稳定的发展平台，也有广阔的成长空间。
接下来和大家说说核心的几点注意事项。在企业文化上，我们始终坚守诚信、创新、协作的价值观，希望大家在工作中相互支持、坦诚沟通，把团队凝聚力转化为前进动力。
规章制度方面，要重点关注考勤与休假制度、保密协议和办公行为规范，这些是保障工作有序开展的基础，详细内容在《员工手册》里有说明，大家一定要认真阅读。
岗位上，每位新同事都有专属导师，后续会帮大家熟悉岗位职责、工作流程和工具使用，遇到任何疑问，随时可以向导师或部门同事请教，我们都会全力支持。
最后，希望大家快速转变角色，沉下心学习积累，在岗位上发挥所长。公司重视每一位员工的成长，会提供必要的培训和晋升机会。
愿我们携手并进，在这个平台上实现个人与公司的共同发展。再次欢迎大家的加入，祝大家工作顺利！谢谢大家！`,
  },
];

const currentPopularTags = computed(() =>
  activeTab.value === 'script' ? scriptPopularTags : ideaPopularTags,
);
const currentPopularTagsLabel = computed(() =>
  activeTab.value === 'script' ? '推荐模板：' : '热门PPT类型：',
);

const maxLength = computed(() => (activeTab.value === 'idea' ? 300 : 8000));

const isTyping = ref(false);
const generatorRef = ref<HTMLElement | null>(null);
const ideaPlaceholder = ref('');

const { stop } = useIntersectionObserver(
  generatorRef,
  ([{ isIntersecting }]) => {
    if (isIntersecting) {
      stop();
      // 使用 setTimeout 延迟以避免 Nuxt 水合期间的值突变导致警告
      setTimeout(() => {
        ideaPlaceholder.value = '';
        const text = '✨ 在此输入您想要制作的PPT内容，如：生成讲解跨境电商机遇与挑战的分享PPT';
        let i = 0;
        const typeInterval = 30;
        const timer = setInterval(() => {
          if (i < text.length) {
            ideaPlaceholder.value += text[i];
            i++;
          } else {
            clearInterval(timer);
          }
        }, typeInterval);
      }, 50);
    }
  },
  {
    threshold: 0.1,
  },
);

const handleApplyTag = (tag: TagContent) => {
  const targetTab = activeTab.value;
  if (targetTab === 'script') {
    scriptInputText.value = tag.content;
    return;
  }
  if (targetTab !== 'idea') return;
  if (isTyping.value) return;
  isTyping.value = true;
  const setTargetText = (value: string) => {
    ideaInputText.value = value;
  };
  const getTargetText = () => ideaInputText.value;
  setTargetText('');
  let i = 0;
  const text = tag.content;
  const typeInterval = 30; // 每个字的间隔，毫秒
  const timer = setInterval(() => {
    if (i < text.length) {
      setTargetText(`${getTargetText()}${text[i]}`);
      i++;
    } else {
      clearInterval(timer);
      isTyping.value = false;
    }
  }, typeInterval);
};

const handleAiGen = async () => {
  await DB.set('aippt_script_boot', {
    type: 'fast_ai',
    content: '',
  });
  window.open(`/editor/page/ppt-script-gen`, '_blank');
};

const handleSend = async () => {
  if (!inputText.value.trim()) {
    if (activeTab.value === 'idea') {
      await handlePromptBoot('生成讲解跨境电商机遇与挑战的分享PPT');
    } else {
      window.open(`/editor/page/ppt-prompt`, '_blank');
    }
    return;
  }

  if (activeTab.value === 'script') {
    await handleScriptBoot(inputText.value, 'default');
  } else if (activeTab.value === 'idea') {
    await handlePromptBoot(inputText.value);
  }
};

const PDF_ACCEPT = 'application/pdf';
const DOC_ACCEPT = 'application/msword';
const DOCX_ACCEPT = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const TEXT_ACCEPT = 'text/plain';

const fileInput = ref<HTMLInputElement | null>(null);
const pendingFile = ref<File | null>(null);

const triggerUpload = (type?: 'doc' | 'pdf' | 'txt') => {
  if (fileInput.value) {
    if (type === 'doc') {
      fileInput.value.accept = '.doc,.docx';
    } else if (type === 'pdf') {
      fileInput.value.accept = '.pdf';
    } else if (type === 'txt') {
      fileInput.value.accept = '.txt';
    } else {
      fileInput.value.accept = '.doc,.docx,.pdf,.txt';
    }
    fileInput.value.click();
  }
};

const handleScriptBoot = async (text: string, mode = 'default') => {
  await DB.set('aippt_script_boot', {
    content: text,
    type: mode,
  });
  const targetUrl = '/editor/page/ppt-script-gen';
  const popup = window.open(targetUrl, '_blank');
  if (!popup) {
    window.location.href = targetUrl;
  }
};

const handlePromptBoot = async (query: string) => {
  await DB.set('aippt_prompt_boot', {
    query,
    scene: filters.find(f => f.key === 'scene')?.selected || '',
    page_range: filters.find(f => f.key === 'pages')?.selected || '',
    audience: filters.find(f => f.key === 'audience')?.selected || '',
  });
  const targetUrl = '/editor/page/ppt-outline';
  const popup = window.open(targetUrl, '_blank');
  if (!popup) {
    window.location.href = targetUrl;
  }
};

const handleFileChange = async (e: Event) => {
  const target = e.target as HTMLInputElement;
  const file = target.files?.[0];
  if (!file) return;

  if (!userStore.user) {
    pendingFile.value = file;
    // 存入 IndexedDB 以解决登录后页面刷新导致状态丢失的问题
    await DB.set('aippt_pending_upload', file);
    show();
    return;
  }

  uploadFile(file);
};

const uploadFile = async (file: File) => {
  if (isParsing.value) return;

  const formData = new FormData();
  formData.append('file', file);
  formData.append('async', '1'); // 异步调用

  if ([DOCX_ACCEPT, DOC_ACCEPT].includes(file.type)) {
    formData.append('file_type', 'word');
  } else if (TEXT_ACCEPT === file.type) {
    formData.append('file_type', 'txt');
  } else if (PDF_ACCEPT === file.type) {
    formData.append('file_type', 'pdf');
  }

  try {
    isParsing.value = true;
    const { code, data, msg } = await parseFile(formData);

    if (code !== 0 || !data) {
      throw new Error(msg || '上传失败');
    }

    const { task_id } = data;

    // 轮询状态
    const pollStatus = async () => {
      const {
        code: statusCode,
        data: statusData,
        msg: statusMsg,
      } = await fetchParseFileResult({ task_id });

      if (statusData.status === 1) {
        // 完成
        await handleScriptBoot(statusData.text);
        // scriptInputText.value = statusData.text;
        // activeTab.value = 'script';
        isParsing.value = false;
      } else if (statusData.status === -1) {
        // 失败
        throw new Error(statusData.text || '解析失败');
      } else if (statusCode !== 0 || !statusData) {
        throw new Error(statusMsg || '获取状态失败');
      } else {
        // 继续轮询
        setTimeout(pollStatus, 2000);
      }
    };

    await pollStatus();
  } catch (err: unknown) {
    const error = err as Error;
    // eslint-disable-next-line no-console
    console.error('Upload/Parse error:', error);
    const { message } = await import('@rendora/common/composables/message');
    message.error(error.message || '文档解析出错，请重试');
    isParsing.value = false;
  } finally {
    // 重置 input 以便同一个文件可以再次触发 change
    if (fileInput.value) {
      fileInput.value.value = '';
    }
  }
};

// 登录后继续调用上传
watch(
  () => userStore.user,
  newUser => {
    if (newUser && pendingFile.value) {
      uploadFile(pendingFile.value);
      pendingFile.value = null;
    }
  },
);

// 初始化时检查是否有待上传的文件
onMounted(async () => {
  // 如果用户已登录，检查 IndexedDB
  if (userStore.user) {
    const file = await DB.get('aippt_pending_upload');
    if (file instanceof File) {
      activeTab.value = tabs[2].value;
      uploadFile(file);
      await DB.del('aippt_pending_upload');
    }
  }
});

// 存放组件引用以支持手动关闭
const dropdownRefs = ref<InstanceType<typeof Dropdown>[]>([]);

// // 使用 capture 模式捕获所有滚动容器的 scroll 事件
// useEventListener(document, 'scroll', closeAllDropdowns, { capture: true, passive: true });
</script>

<style lang="scss" scoped>
.ai-ppt-generator {
  position: relative;
  width: 1200px;
  height: 372px;
}

.ai-ppt-generator__background {
  position: absolute;
  width: 1440px;
  height: 576px;
  top: -140px;
  left: 50%;
  transform: translateX(-50%);
}

.generator-content {
  position: absolute;
  top: 0;
  transform: translateX(-50%);
  left: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.generator-input-wrapper {
  display: flex;
  flex-direction: column;
  gap: 40px;
  align-items: center;
}

// Tab 样式
.input-container {
  display: flex;
  flex-direction: column;
  background: var(--x-back-4);
  border-radius: 12px;
  padding: 4px;
  width: 1000px;
}

.input-tabs {
  display: flex;
  gap: 1px;
  align-items: center;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  overflow: hidden;
  background-color: var(--x-back-4);
}

.tab-item {
  position: relative;
  flex: 1;
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  padding: 11px 48px;
  background: var(--x-back-4);
  cursor: pointer;
  // transition: all 0.2s ease;
  color: var(--x-text-5);
  --tab-curve-radius: 8px;

  /* 基础状态下定义伪元素,透明度为0 */
  &::before,
  &::after {
    content: '';
    position: absolute;
    bottom: 0px;
    width: calc(var(--tab-curve-radius) * 2);
    height: calc(var(--tab-curve-radius) * 2);
    background: transparent;
    opacity: 0;
    z-index: 3;
  }

  &::before {
    left: calc(var(--tab-curve-radius) * -2);
    border-bottom-right-radius: var(--tab-curve-radius);
    box-shadow: var(--tab-curve-radius) 0 0 0 #ffffff;
  }

  &::after {
    right: calc(var(--tab-curve-radius) * -2);
    border-bottom-left-radius: var(--tab-curve-radius);
    box-shadow: calc(var(--tab-curve-radius) * -1) 0 0 0 #ffffff;
  }

  &.active {
    background: var(--x-back-1);
    border-radius: 8px 8px 0 0;
    color: var(--x-text-1);
    // transition: opacity 0.3s ease;

    &::before,
    &::after {
      opacity: 1;
    }
  }

  &:hover:not(.active) {
    background-color: var(--x-back-4);
    color: var(--x-text-3);
    border-radius: 8px 8px 8px 8px;
  }
}

.tab-icon {
  flex-shrink: 0;
  color: var(--x-text-5);
}

.tab-label {
  font-size: 18px;
  font-weight: 400;
  line-height: 26px;
  white-space: nowrap;
}

// 输入框样式
.input-box {
  background: var(--x-back-1);
  padding: 12px;
  width: 100%;
  height: 240px;
  padding-right: 0px;

  // transition: border-radius 0.1s linear;

  // 第一个tab选中：右上角和底部两个角有圆角
  &--idea {
    border-radius: 0 8px 8px 8px;
  }

  // 第二个tab选中：上面两个角和底部两个角有圆角
  &--script {
    border-radius: 8px 8px 8px 8px;
  }

  // 第三个tab选中：左上角和底部两个角有圆角
  &--document {
    border-radius: 8px 0 8px 8px;
  }
}

.upload-section {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 88px;

  .upload-filetype {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    gap: 8px;
    width: 120px;
    height: 120px;
    border: 1px solid var(--x-back-5);
    transition: all 0.2s ease;
    cursor: pointer;
    .upload-icon {
      width: 40px;
      height: 40px;
      font-size: 40px;
    }
    .type-name {
      font-size: 16px;
      font-weight: 400;
      color: var(--x-text-3);
    }

    &--doc:hover {
      border-color: var(--x-back-6);
      background-color: var(--x-back-3);
    }
    &--pdf:hover {
      border-color: var(--x-back-6);
      background-color: var(--x-back-3);
    }
    &--txt:hover {
      border-color: var(--x-back-6);
      background-color: var(--x-back-3);
    }
  }
}

.hidden-file-input {
  display: none;
}

.input-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.text-input {
  width: 100%;
  min-height: 168px;
  padding: 0;
  border: none;
  outline: none;
  resize: none;
  font-size: 16px;
  font-weight: 400;
  line-height: 28px;
  color: var(--x-text-1);
  background: transparent;
  overflow-y: auto;

  &::placeholder {
    color: var(--x-text-7);
  }
}

// 输入框底部
.input-footer {
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
  justify-content: space-between;
  height: 36px;
  width: 968px;
}

.filter-section {
  display: flex;
  gap: 12px;
  align-items: center;
}

.ai-script-section {
  display: flex;
  gap: 8px;
  align-items: center;
  font-size: 16px;
  padding: 6px 12px;
  border-radius: 8px;
  font-weight: 400;
  color: var(--x-text-3);
  border: 1px solid var(--x-back-5);
  background: var(--x-back-1);
  transition: all 0.2s ease;

  .ai-icon {
    color: var(--x-purple-6);
  }

  &:hover {
    border-color: var(--x-purple-6);
  }
}

.filter-item {
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 6px 11px;
  background: var(--x-back-1);
  border: 1px solid var(--x-back-5);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: var(--x-text-3);
  line-height: 22px;

  &:hover {
    border-color: var(--x-purple-6);
  }

  .dropdown-icon {
    flex-shrink: 0;
    color: var(--x-text-3);
    transition: transform 0.2s ease;
    font-size: 18px;
    width: 18px;
    height: 18px;
    &.active {
      transform: rotate(180deg);
    }
  }
}

.filter-icon {
  flex-shrink: 0;
  color: var(--x-text-3);
  font-size: 18px;
  width: 18px;
  height: 18px;
}

.filter-label {
  font-size: 16px;
  font-weight: 400;
  white-space: nowrap;
  color: var(--x-text-3);
  display: flex;
  align-items: center;
  gap: 4px;
}

.action-section {
  display: flex;
  gap: 12px;
  align-items: center;
  height: 36px;
}

.char-count {
  font-size: 16px;
  font-weight: 400;
  line-height: 1;
  color: var(--x-text-7);
  text-align: right;
}

.divider {
  width: 1px;
  height: 20px;
  background: var(--x-back-4);
}

.send-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 6px;
  background: var(--x-text-1);
  border: none;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: white;

  &:hover:not(:disabled) {
    background-color: var(--x-text-3);
  }

  &:disabled {
    cursor: not-allowed;
    background-color: var(--x-text-8);
  }
}

.parsing-status {
  display: flex;
  align-items: center;
  gap: 8px;

  .loading-spinner {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    animation: spin 1s linear infinite;
  }

  .parsing-text {
    font-size: 16px;
    color: var(--x-text-5);
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

// 热门标签
.popular-tags {
  display: flex;
  gap: 4px;
  align-items: center;
  font-size: 16px;
  font-weight: 400;
  line-height: 1;
  color: var(--x-text-3);
}

.tags-label {
  flex-shrink: 0;
}

.tags-list {
  display: flex;
  gap: 12px;
  align-items: center;
}

.tag-item {
  display: flex;
  gap: 4px;
  align-items: center;
  padding: 6px 11px;
  font-size: 16px;
  background: var(--x-back-1);
  border: 1px solid var(--x-back-5);
  color: var(--x-text-3);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--x-back-6);
    background: var(--x-back-3);
  }
}

.tag-emoji {
  flex-shrink: 0;
  text-align: center;
}

.tag-text {
  flex-shrink: 0;
  white-space: nowrap;
  line-height: 22px;
}

.filter-dropdown-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px;
  background-color: var(--x-back-1);

  border-radius: 8px;
  border: 1px solid var(--x-back-4);

  /* 投影 - 中 */
  box-shadow: 0 2px 20px 0 rgba(18, 25, 38, 0.1);

  .filter-dropdown-item {
    font-size: 16px;
    font-weight: 400;
    color: var(--x-text-1);
    padding: 8px;
    border-radius: 8px;
    line-height: 22px;
    cursor: pointer;
    transition: background 0.2s ease;

    &:hover {
      background: var(--x-back-4);
    }

    &.active {
      // color: var(--x-purple-6);
      background: var(--x-back-4);
    }
  }
}
</style>
